import _groupBy from 'lodash/groupBy';
import {
  AuthenticationUserType,
  Provider,
  ProviderAccessType,
  ProviderEntity,
  ProviderStatus,
  ProviderType,
} from '../models/provider';
import { ProviderCapability } from '../models/providerCapability';
import { CredentialsType } from '../models/credentials';

export enum ProviderSortingStrategy {
  BY_POPULAR_FLAG = 'BY_POPULAR_FLAG',
  BY_RANK_PROPERTY = 'BY_RANK_PROPERTY',
}

export type ProviderViewSetGroup = {
  title: string;
  providers: ProviderEntity[];
  capabilities: ProviderCapability[];
  accessTypes: ProviderAccessType[];
  credentialsTypes: CredentialsType[];
  financialInstitutionIds: string[];
  authenticationUserTypes: AuthenticationUserType[];
  popular: boolean;
  rank: number;
  iconUrl?: string;
};

type ProviderViewSetGroups = ProviderViewSetGroup[];
const sortAlphabetically = (viewSetGroups: ProviderViewSetGroups) => {
  return [...viewSetGroups].sort((group, nextGroup) => {
    const titleUppercase = group.title.toUpperCase();
    const nextTitleUppercase = nextGroup.title.toUpperCase();

    if (titleUppercase === nextTitleUppercase) {
      return group.title < nextGroup.title ? -1 : 1;
    }

    return titleUppercase < nextTitleUppercase ? -1 : 1;
  });
};

const sortByPopularity = (viewSetGroups: ProviderViewSetGroups) => {
  return [...viewSetGroups].sort(
    (provider1, provider2) => Number(provider2.popular) - Number(provider1.popular)
  );
};

const sortByRankProperty = (viewSetGroups: ProviderViewSetGroups) => {
  return [...viewSetGroups].sort(
    (provider1, provider2) => Number(provider2.rank) - Number(provider1.rank)
  );
};

const unwantedPrefixes = [
  'Kreissparkasse ',
  'Sparkasse im ',
  'Sparkasse am ',
  'Sparkasse ',
  'Stadtsparkasse ',
  'Postbank (Giro) ',
  'Postbank ',
  'Volksbank Raiffeisenbank in ',
  'Volksbank Raiffeisenbank ',
  'Volksbank im ',
  'Volksbank ',
  'VR-Bank ',
  'VR Bank ',
  'Raiffeisenbank im ',
  'Raiffeisen-Volksbank ',
  'Raiffeisenbank ',
];

export const getProviderPrefix = (title: string) =>
  unwantedPrefixes.find((prefix) => title.startsWith(prefix));

export const getUnprefixedBranch = (title: string) => {
  const prefixedWith = getProviderPrefix(title);

  return prefixedWith ? title.replace(prefixedWith, '') : title;
};

export type AlphabeticalKey = string;

/**
 * This function will group provider branches based on the first character in their unprefixed name.
 */
export const getAlphabeticalProviderGroups = (
  providerViewSetGroups: ProviderViewSetGroup[]
): [AlphabeticalKey, ProviderViewSetGroup[]][] => {
  const groupedProviders = _groupBy(providerViewSetGroups, (p) => {
    const title = getUnprefixedBranch(p.title);

    // In case the title is an empty string, fallback to group A
    return title.length > 0 ? title[0].toUpperCase() : 'A';
  });

  return Object.entries(groupedProviders);
};

/**
 * Sorts the inner group of provider branches using their unprefixed name in alphabetical order.
 * Sorts the alphabetical group key in alphabetical order.
 */
export const sortAlphabeticalProviderGroups = (
  groupedProviders: [AlphabeticalKey, ProviderViewSetGroup[]][]
): [AlphabeticalKey, ProviderViewSetGroup[]][] => {
  groupedProviders.forEach(([_group, providerViewSets]) => {
    providerViewSets.sort((a, b) =>
      getUnprefixedBranch(a.title).localeCompare(getUnprefixedBranch(b.title))
    );
  });

  return groupedProviders.sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
};

/**
 * Takes providers and outputs the most s ignificant image url.
 * @param providers
 */
export const getIconUrl = (providers: ProviderEntity[]) => {
  const images = providers.map((provider) => Provider.getIcon(provider));

  const nonPlaceHolderImage = images.find((i) => !/placeholder.png/.test(i));

  if (nonPlaceHolderImage) return nonPlaceHolderImage;

  const validPng = images.find((i) => /.png/.test(i));

  if (validPng) return validPng;

  return 'https://cdn.tink.se/provider-images/placeholder.png';
};

/**
 * Returns either the groupDisplayName or (if not set) the financialInstitutionName
 * of a provider.
 * @param provider
 */
export const getNormalizedProviderName = (provider: ProviderEntity): string => {
  return provider.groupDisplayName || provider.financialInstitutionName;
};

/**
 * ProviderViewSet is used to go from an array of providers to a group of providers
 * It works like this for providers = [a1,a2,b,c,d], viewset = new ProviderViewSet(providers)
 * viewset.groupByType(), where Type is either A or B, returns [{...props,"TYPE_A":[a1,b,c]}, {...otherProps, "TYPE_B":[a2,d]}]
 */

export class ProviderViewSet {
  private filteredProviders: ProviderEntity[];

  private providerSortingStrategy: ProviderSortingStrategy;

  constructor(
    providers: ProviderEntity[],
    version: string,
    types: ProviderType[] = [
      ProviderType.BANK,
      ProviderType.BROKER,
      ProviderType.CREDIT_CARD,
      ProviderType.OTHER,
      ProviderType.TEST,
      ProviderType.FIRST_PARTY,
    ],
    status: ProviderStatus[] = [ProviderStatus.ENABLED, ProviderStatus.TEMPORARY_DISABLED],
    providerSortingStrategy: ProviderSortingStrategy = ProviderSortingStrategy.BY_POPULAR_FLAG
  ) {
    this.filteredProviders = providers
      .filter((p) => types.includes(p.type))
      .filter((p) => status.includes(p.status))
      .filter((p) => !Provider.isBlocklisted(p, version));
    this.providerSortingStrategy = providerSortingStrategy;
  }

  static buildProviderViewSetGroups({
    providersMap,
    shouldSortAlphabetically = false,
  }: {
    providersMap: Record<string, ProviderEntity[]>;
    shouldSortAlphabetically?: boolean;
  }): ProviderViewSetGroups {
    const viewSetGroup: ProviderViewSetGroups = Object.keys(providersMap).map(
      (currentGroupName) => {
        const allProvidersInGroup = providersMap[currentGroupName];
        const isGroupPopular = allProvidersInGroup.some((value) => value.popular);
        const iconToUse = allProvidersInGroup.find(
          (value) => !/placeholder.png/.test(value.images.icon)
        )?.images.icon;
        const groupRank = Math.max(...allProvidersInGroup.map((p) => p.rank), 0);

        const groupAccessTypes: Set<ProviderAccessType> = new Set(
          allProvidersInGroup.map((provider) => provider.accessType)
        );
        const groupCredentialsTypes: Set<CredentialsType> = new Set(
          allProvidersInGroup.map((provider) => provider.credentialsType)
        );
        const groupFinancialInstitutionIds: Set<string> = new Set(
          allProvidersInGroup.map((provider) => provider.financialInstitutionId)
        );
        const groupAuthenticationUserTypes: Set<AuthenticationUserType> = new Set(
          allProvidersInGroup.map((provider) => provider.authenticationUserType)
        );

        const groupCapabilities: Set<ProviderCapability> = new Set();

        allProvidersInGroup.forEach((provider) => {
          provider.capabilities.forEach((capability) => {
            groupCapabilities.add(capability);
          });
        });

        return {
          title: currentGroupName,
          providers: allProvidersInGroup,
          capabilities: Array.from(groupCapabilities),
          accessTypes: Array.from(groupAccessTypes),
          credentialsTypes: Array.from(groupCredentialsTypes),
          financialInstitutionIds: Array.from(groupFinancialInstitutionIds),
          authenticationUserTypes: Array.from(groupAuthenticationUserTypes),
          popular: isGroupPopular,
          iconUrl: iconToUse || 'https://cdn.tink.se/provider-images/placeholder.png',
          rank: groupRank,
        };
      }
    );

    if (shouldSortAlphabetically) {
      return sortAlphabetically(viewSetGroup);
    }

    return viewSetGroup;
  }

  groupByGroupDisplayName() {
    const groupedProviders = _groupBy(this.filteredProviders, (p) => {
      return getNormalizedProviderName(p);
    });

    const { providerSortingStrategy } = this;

    const viewSetGroups = ProviderViewSet.buildProviderViewSetGroups({
      providersMap: groupedProviders,
      shouldSortAlphabetically: true,
    });

    if (providerSortingStrategy === ProviderSortingStrategy.BY_POPULAR_FLAG) {
      return sortByPopularity(viewSetGroups);
    }

    if (providerSortingStrategy === ProviderSortingStrategy.BY_RANK_PROPERTY) {
      return sortByRankProperty(viewSetGroups);
    }

    return viewSetGroups;
  }

  groupByDisplayName(shouldSortAlphabetically = true) {
    const groupedProviders = _groupBy(this.filteredProviders, (p) => {
      return p.displayName;
    });

    return ProviderViewSet.buildProviderViewSetGroups({
      providersMap: groupedProviders,
      shouldSortAlphabetically,
    });
  }

  groupByFinancialInstitutionId() {
    const groupedProviders = _groupBy(this.filteredProviders, (p) => {
      return p.financialInstitutionId;
    });

    return ProviderViewSet.buildProviderViewSetGroups({
      providersMap: groupedProviders,
      shouldSortAlphabetically: true,
    });
  }

  groupByAuthenticationUserType() {
    const groupedProviders = _groupBy(this.filteredProviders, (p) => {
      return p.authenticationUserType;
    });

    return ProviderViewSet.buildProviderViewSetGroups({
      providersMap: groupedProviders,
    });
  }

  groupByAccessType() {
    const groupedProviders = _groupBy(this.filteredProviders, (p) => {
      return p.accessType;
    });

    return ProviderViewSet.buildProviderViewSetGroups({
      providersMap: groupedProviders,
      shouldSortAlphabetically: true,
    });
  }

  groupByCredentialsType() {
    const groupedProviders = _groupBy(this.filteredProviders, (p) => {
      return p.credentialsType;
    });

    return ProviderViewSet.buildProviderViewSetGroups({ providersMap: groupedProviders });
  }
}
